/**
 * Avatar — universal user-photo component.
 *
 * Renders the user's uploaded photo if available, otherwise a colored circle
 * with the person's initials. Picture URLs starting with "/api/avatar/..."
 * are prefixed with REACT_APP_BACKEND_URL so they render on production where
 * the API is on a different host than the frontend.
 *
 * Props:
 *   name      — display name (used to derive initials + bg color)
 *   picture   — full URL or backend path; optional
 *   size      — px (default 28)
 *   testId    — data-testid override
 */
const PALETTE = [
  "#FF7A59", "#FFB400", "#22C55E", "#06B6D4", "#3B82F6",
  "#8B5CF6", "#EC4899", "#F43F5E", "#14B8A6", "#A16207",
];

function colorFor(seed) {
  if (!seed) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function initialsFor(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function resolvePicture(picture) {
  if (!picture) return null;
  if (picture.startsWith("/api/")) {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}${picture}`;
  }
  return picture;
}

export default function Avatar({ name = "", picture = null, size = 28, testId, className = "" }) {
  const src = resolvePicture(picture);
  const initials = initialsFor(name);
  const bg = colorFor(name || initials);
  const fontSize = Math.max(9, Math.round(size * 0.42));

  if (src) {
    return (
      <img
        src={src}
        alt={name || "avatar"}
        className={`rounded-full object-cover ${className}`}
        style={{ width: size, height: size, border: "1px solid var(--border)" }}
        data-testid={testId || "avatar-img"}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }

  return (
    <span
      className={`inline-grid place-items-center rounded-full font-bold select-none ${className}`}
      style={{
        width: size,
        height: size,
        background: bg,
        color: "#fff",
        fontSize,
        letterSpacing: "-0.02em",
        flexShrink: 0,
      }}
      title={name || ""}
      data-testid={testId || "avatar-initials"}
      aria-label={name ? `${name} avatar` : "avatar"}
    >
      {initials}
    </span>
  );
}
